import { useAppStorage, useObservable } from '@aesop-fables/scrinium';
import { Card, CardContent, Typography, Button, Link, Box, Skeleton } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { ToastbarContext } from '../../App';
import { Column, Spacer, DownloadButton, Row } from '../../components';
import { OrganizationCompartments, organizationStorageKey } from '../../data/organization';
import { useDeviceType } from '../../hooks/useDeviceType';
import { OrganizationData } from '../../models/OrganizationData';
import { Toast } from '../../models/Toast';

export const MemberSignUpCard: React.FC = () => {
  const appStorage = useAppStorage();
  const { isMobile, isTablet } = useDeviceType();
  const { setToast } = useContext(ToastbarContext);
  const [, forceUpdate] = React.useReducer(() => ({}), {});
  const [imageLoaded, setImageLoaded] = useState<boolean>(false);
  const isCompactDesktop =
    window.innerWidth <= 1200 || document.documentElement.clientWidth <= 1200;

  const organizationDataCache =
    appStorage.retrieve<OrganizationCompartments>(organizationStorageKey);
  const orgData = useObservable(organizationDataCache.observe$<OrganizationData>('organization'));
  const { url: orgUrl, qrUri: orgQR, name: orgName } = orgData || {};

  const qrImage = (
    <Box sx={{ position: 'relative', width: 'fit-content' }}>
      {!imageLoaded && <Skeleton variant='rectangular' width={103} height={103} />}
      <img
        src={orgQR}
        alt='QR Code'
        style={{
          border: '2px solid black',
          display: imageLoaded ? 'block' : 'none',
        }}
        onLoad={() => setImageLoaded(true)}
      />
    </Box>
  );

  useEffect(() => {
    const handleResize = () => forceUpdate();
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const onCopy = () => {
    navigator.clipboard.writeText(orgUrl ?? '');
    setToast(new Toast({ message: 'Copied to clipboard!', open: true, severity: 'success' }));
  };

  const onDownload = () => {
    if (!orgQR) {
      setToast(new Toast({ message: 'QR code not found.', open: true, severity: 'error' }));
      return;
    }
    try {
      const link = document.createElement('a');
      link.href = orgQR;
      link.download = `${orgName}_QR.png`;
      link.click();
    } catch (err) {
      console.error(err);
      setToast(new Toast({ message: 'Error downloading QR code.', open: true, severity: 'error' }));
    }
  };

  if (isMobile || isTablet) {
    return (
      <Column>
        <Card variant='ghost' color='primary'>
          <CardContent className='yearlyPrice'>
            <Column style={{ alignItems: 'center' }}>
              <Typography variant='p16' color='secondary.dark'>
                Copy URL
              </Typography>
              <Link
                sx={{
                  textDecoration: 'underline',
                  maxWidth: '100%',
                }}
                variant='p12'
                color='primary'
                href={orgUrl}
                target='_blank'
                rel='noreferrer'>
                {orgUrl}
              </Link>
              <Spacer height='xxxs' />
              <Button color='secondary' onClick={onCopy}>
                <Typography variant='p12'>Copy to clipboard</Typography>
              </Button>
            </Column>
          </CardContent>
        </Card>
        <Spacer height='xs' />
        <Card variant='ghost' color='primary' style={{ flex: 1 }}>
          <CardContent className='yearlyPrice'>
            <Column style={{ alignItems: 'center' }}>
              <Typography variant='p16' color='secondary.dark'>
                Download QR Code
              </Typography>
              <Spacer height='xxxs' />
              {qrImage}
              <Spacer height='xxxs' />
              <DownloadButton onDownload={onDownload} textVariant='p12' style={{ width: 105 }} />
            </Column>
            <Spacer width='xxl' />
          </CardContent>
        </Card>
      </Column>
    );
  }

  return (
    <Row style={{ flexWrap: 'wrap', display: 'flex', gap: '16px' }}>
      <Card
        variant='ghost'
        color='primary'
        style={{
          flex: 2,
          ...(isCompactDesktop && { flex: 1 }),
        }}>
        <CardContent
          className='memberSignupCard'
          style={{
            height: '100%',
            alignContent: 'center',
          }}>
          <Row style={{ height: '85%' }}>
            <Column style={{ justifyContent: 'space-between' }}>
              <Typography variant='p20' color='secondary.dark'>
                Copy URL
              </Typography>
              <Button sx={{ whiteSpace: 'nowrap' }} color='secondary' onClick={onCopy}>
                <Typography variant='p14'>Copy to clipboard</Typography>
              </Button>
            </Column>
            <Spacer width='sm' />
            <Link
              className='truncate-three-lines'
              sx={{
                width: '90%',
                alignSelf: 'flex-end',
                justifyContent: 'flex-start',
                textDecoration: 'underline',
              }}
              variant='p20'
              color='primary'
              href={orgUrl}
              target='_blank'
              rel='noreferrer'>
              {orgUrl}
            </Link>
          </Row>
        </CardContent>
      </Card>

      <Card
        variant='ghost'
        color='primary'
        style={{
          flex: 1,
          ...(isCompactDesktop && { flex: 1 }),
        }}>
        <CardContent
          className='memberSignupCard'
          style={{
            height: '100%',
            alignContent: 'center',
          }}>
          <Row style={{ justifyContent: 'space-between', height: '85%' }}>
            <Column style={{ justifyContent: 'space-between' }}>
              <Typography variant='p20' color='secondary.dark'>
                Download QR Code
              </Typography>
              <DownloadButton onDownload={onDownload} textVariant='p14' style={{ height: 45 }} />
            </Column>
            <Spacer width='md' />
            {qrImage}
          </Row>
        </CardContent>
      </Card>
    </Row>
  );
};

export default MemberSignUpCard;
